// src/store/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

// Authentication constants
export const AUTH_STORAGE_KEY = 'auth_token';
export const USER_STORAGE_KEY = 'auth_user';

// User roles constants
export const USER_ROLES = {
  USER: 'user',
  DRIVER: 'driver',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin'
};

export const ROLE_HIERARCHY = {
  [USER_ROLES.USER]: 1,
  [USER_ROLES.DRIVER]: 2,
  [USER_ROLES.ADMIN]: 3,
  [USER_ROLES.SUPER_ADMIN]: 4
};

// Token management utilities
export const tokenUtils = {
  setToken: (token) => {
    if (token) {
      localStorage.setItem(AUTH_STORAGE_KEY, token);
      Cookies.set(AUTH_STORAGE_KEY, token, { 
        expires: 7, // 7 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
    }
  },
  
  getToken: () => {
    return localStorage.getItem(AUTH_STORAGE_KEY) || Cookies.get(AUTH_STORAGE_KEY);
  },
  
  removeToken: () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    Cookies.remove(AUTH_STORAGE_KEY);
  },
  
  setUser: (user) => {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    }
  },
  
  getUser: () => {
    const userStr = localStorage.getItem(USER_STORAGE_KEY);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  },
  
  isTokenValid: (token) => {
    if (!token) return false;
    
    try {
      // Basic JWT structure validation
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }
};

// Async thunks for authentication actions
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      // For now, simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock authentication logic
      const { email, password } = credentials;
      
      // Mock user data - replace with actual API response
      if (email === 'admin@example.com' && password === 'admin123') {
        const mockUser = {
          id: '1',
          email: 'admin@example.com',
          name: 'Admin User',
          role: USER_ROLES.ADMIN,
          avatar: null,
          permissions: ['dashboard_view', 'users_manage', 'cars_manage']
        };

        // Create a token that expires in 24 hours
        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "1",
          email: "admin@example.com",
          role: "admin",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      } else if (email === 'user@example.com' && password === 'user123') {
        const mockUser = {
          id: '2',
          email: 'user@example.com',
          name: 'Regular User',
          role: USER_ROLES.USER,
          avatar: null,
          permissions: ['dashboard_view']
        };

        // Create a token that expires in 24 hours
        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "2",
          email: "user@example.com",
          role: "user",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      } else if (email === 'superadmin@example.com' && password === 'super123') {
        const mockUser = {
          id: '3',
          email: 'superadmin@example.com',
          name: 'Super Admin',
          role: USER_ROLES.SUPER_ADMIN,
          avatar: null,
          permissions: ['dashboard_view', 'users_manage', 'users_delete', 'cars_manage', 'system_admin', 'user_roles_manage']
        };

        // Create a token that expires in 24 hours
        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "3",
          email: "superadmin@example.com",
          role: "super_admin",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Driver Authentication Credentials
      // Approved Driver - Full access to all driver features
      else if (email === 'john.smith@example.com' && password === 'driver123') {
        const mockUser = {
          id: '4',
          email: 'john.smith@example.com',
          name: 'John Smith',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'trips_view', 'trips_manage', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "4",
          email: "john.smith@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Active Driver - Currently online and accepting trips
      else if (email === 'maria.garcia@example.com' && password === 'driver123') {
        const mockUser = {
          id: '5',
          email: 'maria.garcia@example.com',
          name: 'Maria Garcia',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'trips_view', 'trips_manage', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "5",
          email: "maria.garcia@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Pending Driver - Awaiting admin approval
      else if (email === 'david.johnson@example.com' && password === 'driver123') {
        const mockUser = {
          id: '6',
          email: 'david.johnson@example.com',
          name: 'David Johnson',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "6",
          email: "david.johnson@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Top Performing Driver - Highest ratings and earnings
      else if (email === 'sarah.wilson@example.com' && password === 'driver123') {
        const mockUser = {
          id: '7',
          email: 'sarah.wilson@example.com',
          name: 'Sarah Wilson',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'trips_view', 'trips_manage', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "7",
          email: "sarah.wilson@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Driver with Issues - Expired documents, lower performance
      else if (email === 'mike.brown@example.com' && password === 'driver123') {
        const mockUser = {
          id: '8',
          email: 'mike.brown@example.com',
          name: 'Mike Brown',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'trips_view', 'trips_manage', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "8",
          email: "mike.brown@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Additional Test Driver Credentials for comprehensive testing

      // Rejected Driver - Application was rejected
      else if (email === 'rejected.driver@example.com' && password === 'driver123') {
        const mockUser = {
          id: '9',
          email: 'rejected.driver@example.com',
          name: 'Rejected Driver',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "9",
          email: "rejected.driver@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Suspended Driver - Account temporarily suspended
      else if (email === 'suspended.driver@example.com' && password === 'driver123') {
        const mockUser = {
          id: '10',
          email: 'suspended.driver@example.com',
          name: 'Suspended Driver',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "10",
          email: "suspended.driver@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      }

      // Inactive Driver - Not currently active
      else if (email === 'inactive.driver@example.com' && password === 'driver123') {
        const mockUser = {
          id: '11',
          email: 'inactive.driver@example.com',
          name: 'Inactive Driver',
          role: USER_ROLES.DRIVER,
          avatar: null,
          permissions: ['dashboard_view', 'notifications_view']
        };

        const expirationTime = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
        const mockPayload = {
          sub: "11",
          email: "inactive.driver@example.com",
          role: "driver",
          exp: expirationTime
        };
        const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;

        return { user: mockUser, token: mockToken };
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      // TODO: Call API to invalidate token on server
      // const token = getState().auth.token;
      // await api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
      
      // Clear local storage
      tokenUtils.removeToken();
      
      return true;
    } catch (error) {
      // Even if server logout fails, clear local storage
      tokenUtils.removeToken();
      return true;
    }
  }
);

// Initial state
const initialState = {
  user: tokenUtils.getUser(),
  token: tokenUtils.getToken(),
  isAuthenticated: Boolean(tokenUtils.getToken() && tokenUtils.isTokenValid(tokenUtils.getToken())),
  isLoading: false,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null
};

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear authentication state (for manual logout)
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      tokenUtils.removeToken();
    },
    
    // Update user profile
    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        tokenUtils.setUser(state.user);
      }
    },
    
    // Check token validity
    validateToken: (state) => {
      const token = tokenUtils.getToken();
      const isValid = token && tokenUtils.isTokenValid(token);
      
      if (!isValid) {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        tokenUtils.removeToken();
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        const { user, token } = action.payload;
        
        state.user = user;
        state.token = token;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
        state.loginAttempts = 0;
        
        // Store in localStorage/cookies
        tokenUtils.setToken(token);
        tokenUtils.setUser(user);
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.loginAttempts += 1;
        state.lastLoginAttempt = new Date().toISOString();
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      
      // Logout cases
      .addCase(logoutAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(logoutAsync.rejected, (state) => {
        // Even if logout fails on server, clear local state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      });
  }
});

export const { clearError, clearAuth, updateUserProfile, validateToken } = authSlice.actions;
export default authSlice.reducer;
